import { Injectable, inject, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '@/environments/environment';
import { UserProfileViewModel } from '@/app/proxy/proxies';
import { AppConfigService } from './app-config.service';
import { getMixpanelApiHost } from '../mixpanel-api-host';

interface AnalyticsState {
    mixpanel: {
        initialized: boolean;
    };
    googleAnalytics: {
        initialized: boolean;
        measurementId: string | null;
    };
    userId: string | null;
    sessionStart: number;
}

// Global gtag function declaration
declare global {
    interface Window {
        gtag: (command: string, ...args: unknown[]) => void;
        dataLayer: unknown[];
    }
}

/**
 * Unified Analytics Service - Handles both Mixpanel and Google Analytics
 * Auto-tracks user sessions and route changes for both platforms
 * No manual configuration needed - completely dynamic and maintenance-free
 */
@Injectable({
    providedIn: 'root',
})
export class UnifiedAnalyticsService {
    private readonly router = inject(Router);
    private readonly appConfigService = inject(AppConfigService);
    private readonly destroyRef = inject(DestroyRef);

    // Simple signal-based state
    private readonly _state = signal<AnalyticsState>({
        mixpanel: {
            initialized: false,
        },
        googleAnalytics: {
            initialized: false,
            measurementId: null,
        },
        userId: null,
        sessionStart: Date.now(),
    });

    // Public accessors
    readonly initialized = computed(
        () => this._state().mixpanel.initialized || this._state().googleAnalytics.initialized
    );

    constructor() {
        this.initializeWhenReady();
    }

    /**
     * Initialize both Mixpanel and Google Analytics when app config is ready
     */
    private async initializeWhenReady(): Promise<void> {
        try {
            // Wait for app config to be ready
            await this.waitForAppConfig();

            // Initialize both services in parallel
            await Promise.allSettled([this.initializeMixpanel(), this.initializeGoogleAnalytics()]);

            // Start automatic tracking if any service is initialized
            if (this.initialized()) {
                this.startAutoTracking();
            }
        } catch (error) {
            console.warn('Analytics: Failed to initialize:', error);
        }
    }

    /**
     * Initialize Mixpanel
     */
    private async initializeMixpanel(): Promise<void> {
        try {
            const mixpanelConfig = this.getMixpanelConfigFromConfig();

            if (!mixpanelConfig.projectToken) {
                return;
            }

            this._state.update((s) => ({
                ...s,
                mixpanel: { initialized: true },
            }));
        } catch (error) {
            console.warn('Mixpanel: Failed to initialize:', error);
        }
    }

    /**
     * Initialize Google Analytics
     */
    private async initializeGoogleAnalytics(): Promise<void> {
        try {
            const gaConfig = this.getGoogleAnalyticsConfigFromConfig();

            if (!gaConfig) {
                return;
            }

            // Initialize dataLayer
            if (!window.dataLayer) {
                window.dataLayer = [];
            }

            // Define gtag function
            window.gtag = function () {
                window.dataLayer.push(arguments);
            };

            // Set initial timestamp
            window.gtag('js', new Date());

            // Load GA script
            await this.loadGoogleAnalyticsScript(gaConfig);

            // Configure GA
            window.gtag('config', gaConfig, {
                page_title: document.title,
                page_location: window.location.href,
                send_page_view: false,
            });

            this._state.update((s) => ({
                ...s,
                googleAnalytics: {
                    initialized: true,
                    measurementId: gaConfig,
                },
            }));
        } catch (error) {
            console.warn('Google Analytics: Failed to initialize:', error);
        }
    }

    /**
     * Load Google Analytics script
     */
    private loadGoogleAnalyticsScript(measurementId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const existingScript = document.querySelector(
                `script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`
            );
            if (existingScript) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Google Analytics script'));

            document.head.appendChild(script);
        });
    }

    /**
     * Wait for app configuration to be ready
     */
    private async waitForAppConfig(): Promise<void> {
        // Simple polling approach - could be improved with observables
        for (let i = 0; i < 50; i++) {
            try {
                const hasConfig =
                    this.appConfigService.mixpanelProjectToken && this.appConfigService.googleAnalyticsMeasurementId;
                if (hasConfig) return;
            } catch (error) {
                // Config not ready yet
            }
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
        throw new Error('App configuration timeout');
    }

    /**
     * Get Mixpanel configuration from backend
     */
    private getMixpanelConfigFromConfig(): { projectToken: string | null; baseUrl: string | null } {
        try {
            const mixpanelConfig = this.appConfigService.mixpanelConfig;
            return {
                projectToken: mixpanelConfig.projectToken || null,
                baseUrl: mixpanelConfig.baseUrl || null,
            };
        } catch {
            return { projectToken: null, baseUrl: null };
        }
    }

    /**
     * Get project token from backend configuration
     */
    private getProjectTokenFromConfig(): string | null {
        return this.getMixpanelConfigFromConfig().projectToken;
    }

    private getGoogleAnalyticsConfigFromConfig(): string | null {
        try {
            return this.appConfigService.googleAnalyticsMeasurementId || null;
        } catch {
            return null;
        }
    }

    /**
     * Start automatic tracking of routes and sessions for both platforms
     */
    private startAutoTracking(): void {
        if (!this.initialized()) return;

        // Track route changes automatically
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((event) => {
                const navigationEvent = event as NavigationEnd;
                this.trackPageVisit(navigationEvent.urlAfterRedirects || navigationEvent.url);
            });
    }

    /**
     * Track a page visit with automatic route categorization for both platforms
     */
    private trackPageVisit(url: string): void {
        if (!this.initialized()) return;

        const routeInfo = this.categorizeRoute(url);
        const eventData = {
            page_url: url,
            page_name: routeInfo.name,
            page_category: routeInfo.category,
            page_section: routeInfo.section,
            page_depth: this.getRouteDepth(url),
            page_type: this.detectPageType(url),
            timestamp: new Date().toISOString(),
            session_duration: this.getSessionDuration(),
            referrer: document.referrer || 'direct',
        };

        // Track in Google Analytics
        if (this._state().googleAnalytics.initialized && window.gtag) {
            const cleanUrl = url.split('?')[0]; // Remove query params for privacy
            window.gtag('config', this._state().googleAnalytics.measurementId!, {
                page_title: routeInfo.name || document.title,
                page_location: window.location.origin + cleanUrl,
            });

            // Also track as custom event
            window.gtag('event', 'page_view', {
                page_category: routeInfo.category,
                page_section: routeInfo.section,
                page_type: this.detectPageType(url),
            });
        }
    }

    /**
     * 100% Autonomous route categorization - learns from route structure
     */
    private categorizeRoute(url: string): { name: string; category: string; section: string } {
        const pathSegments = url.split('/').filter((segment) => segment.length > 0);

        if (pathSegments.length === 0) {
            return { name: 'Home', category: 'landing', section: 'main' };
        }

        const firstSegment = pathSegments[0];
        const category = this.inferCategoryFromSegment(firstSegment);
        const section = pathSegments[1] || 'main';
        const name = this.generatePageName(pathSegments);

        return { name, category, section };
    }

    /**
     * Infer category from URL segment using domain knowledge
     */
    private inferCategoryFromSegment(segment: string): string {
        const categoryMap: Record<string, string> = {
            auth: 'authentication',
            login: 'authentication',
            register: 'authentication',
            account: 'user_management',
            profile: 'user_management',
            settings: 'user_management',
            lands: 'property_management',
            plantations: 'property_management',
            properties: 'property_management',
            services: 'business_services',
            weather: 'business_services',
            soil: 'business_services',
            pest: 'business_services',
            support: 'customer_support',
            help: 'customer_support',
            contact: 'customer_support',
            notifications: 'user_engagement',
            dashboard: 'analytics',
            reports: 'analytics',
        };

        return categoryMap[segment.toLowerCase()] || 'general';
    }

    /**
     * Generate human-readable page name from path segments
     */
    private generatePageName(segments: string[]): string {
        return segments.map((segment) => this.humanizeSegment(segment)).join(' - ');
    }

    /**
     * Convert URL segment to human-readable format
     */
    private humanizeSegment(segment: string): string {
        return segment
            .replace(/-/g, ' ')
            .replace(/_/g, ' ')
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    /**
     * Calculate route depth for analytics
     */
    private getRouteDepth(url: string): number {
        return url.split('/').filter((segment) => segment.length > 0).length;
    }

    /**
     * Detect page type based on URL patterns
     */
    private detectPageType(url: string): string {
        if (url.includes('/edit') || url.includes('/update')) return 'edit';
        if (url.includes('/create') || url.includes('/new')) return 'create';
        if (url.includes('/list') || url.includes('/browse')) return 'list';
        if (url.includes('/detail') || url.match(/\/\d+$/)) return 'detail';
        if (url.includes('/dashboard')) return 'dashboard';
        if (url.includes('/settings')) return 'settings';
        return 'page';
    }

    /**
     * Detect environment name for analytics
     */
    private detectEnvironmentName(): string {
        if (environment.apiBaseUrl.toLowerCase().includes('api.doktar.io')) return 'production';
        if (environment.apiBaseUrl.toLowerCase().includes('api.doktar.dev')) return 'development';
        if (environment.apiBaseUrl.toLowerCase().includes('api.doktar.cc')) return 'test';
        return 'local';
    }

    /**
     * Detect app version (could be enhanced with actual version info)
     */
    private detectAppVersion(): string {
        // Could be enhanced to read from package.json or environment
        const packageJson = require('../../../../package.json');
        return packageJson.version;
    }

    /**
     * Get current session duration in seconds
     */
    private getSessionDuration(): number {
        return Math.floor((Date.now() - this._state().sessionStart) / 1000);
    }

    /**
     * Track when user identifies (login)
     */
    identifyUser(user: UserProfileViewModel): void {
        if (!this.initialized() || !user.id) return;

        // Identify in Google Analytics (privacy-safe)
        if (this._state().googleAnalytics.initialized && window.gtag) {
            window.gtag('config', this._state().googleAnalytics.measurementId!, {
                user_id: user.id?.toString(),
            });

            // Set user properties (no PII)
            window.gtag('event', 'login', {
                method: 'doktar_login',
            });
        }

        this._state.update((s) => ({ ...s, userId: user.id?.toString() || null }));

        // Track sign in event
        this.track('User Signed In', {
            user_id: user.id,
            session_duration: this.getSessionDuration(),
        });
    }

    /**
     * Track when user signs out
     */
    userSignedOut(): void {
        if (!this.initialized()) return;

        // Track sign out event
        this.track('User Signed Out', {
            user_id: this._state().userId,
            session_duration: this.getSessionDuration(),
        });

        // Clear Google Analytics user ID
        if (this._state().googleAnalytics.initialized && window.gtag) {
            window.gtag('config', this._state().googleAnalytics.measurementId!, {
                user_id: null,
            });
        }

        this._state.update((s) => ({ ...s, userId: null, sessionStart: Date.now() }));
    }

    /**
     * Public method for custom event tracking (sends to both Mixpanel and GA)
     */
    track(eventName: string, properties: Record<string, unknown> = {}): void {
        // Track in Google Analytics
        if (this._state().googleAnalytics.initialized && window.gtag) {
            // Sanitize event name for GA4
            const gaEventName = this.sanitizeGAEventName(eventName);
            const gaProperties = this.sanitizeGAProperties(properties);

            window.gtag('event', gaEventName, gaProperties);
        }
    }

    /**
     * Sanitize event name for Google Analytics
     */
    private sanitizeGAEventName(eventName: string): string {
        return eventName
            .toLowerCase()
            .replace(/[^a-z0-9_]/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '');
    }

    /**
     * Sanitize properties for Google Analytics
     */
    private sanitizeGAProperties(properties: Record<string, unknown>): Record<string, unknown> {
        const sanitized: Record<string, unknown> = {};

        Object.entries(properties).forEach(([key, value]) => {
            const sanitizedKey = key
                .toLowerCase()
                .replace(/[^a-z0-9_]/g, '_')
                .replace(/_+/g, '_')
                .replace(/^_|_$/g, '');

            if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                sanitized[sanitizedKey] = value;
            } else if (value !== null && value !== undefined) {
                sanitized[sanitizedKey] = String(value);
            }
        });

        return sanitized;
    }

    /**
     * Get initialization status for debugging
     */
    getStatus(): unknown {
        const state = this._state();
        return {
            mixpanel: {
                initialized: state.mixpanel.initialized,
            },
            googleAnalytics: {
                initialized: state.googleAnalytics.initialized,
                measurementId: state.googleAnalytics.measurementId,
            },
            user: {
                hasUser: !!state.userId,
                userId: state.userId,
            },
            session: {
                sessionStart: state.sessionStart,
                sessionDuration: this.getSessionDuration(),
            },
        };
    }
}
